import { z } from 'zod';
import { uuid, isoTimestamp, syncColumns } from '../schemas/common.js';
/**
 * Row and protocol shapes for the Schaak module (docs/SCHAAK.md §4). The
 * generic-sync-core shape: `household_id` derived server-side, the module's own
 * mutation union and pull response over its own tables. Every row is one
 * learner's own (`created_by`), with an id derived by `schaakId`.
 */
/** The tables a phone writes. `schaak_games`/`schaak_mistakes` are server-owned and only pulled. */
export const SCHAAK_SYNC_TABLES = ['schaak_cards', 'schaak_profile', 'schaak_woodpecker', 'schaak_accounts'];
export const schaakSyncTable = z.enum(SCHAAK_SYNC_TABLES);
export const schaakCardRow = syncColumns.extend({
    id: uuid,
    kind: z.enum(['puzzle', 'mistake', 'drill', 'opening']),
    ref: z.string().min(1).max(100),
    theme: z.string().max(40).nullish(),
    box: z.number().int().min(0).max(20),
    due_at: isoTimestamp.nullish(),
    attempts: z.number().int().min(0),
    solved: z.number().int().min(0),
    failed: z.number().int().min(0),
    first_result: z.enum(['solved', 'failed']).nullish(),
    last_at: isoTimestamp.nullish(),
    /** His own diagnosis of a mistake (046). */
    reason: z.enum(['threat', 'hanging', 'missed', 'calc', 'rushed', 'unsure']).nullish(),
    created_by: uuid.nullish(),
});
export const schaakProfileRow = syncColumns.extend({
    id: uuid,
    rating: z.number().int().min(0).max(4000),
    rating_games: z.number().int().min(0),
    /** Day log (046): rating at day's end, attempted, solved. */
    days: z
        .array(z.object({ d: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), r: z.number().int(), n: z.number().int().min(0), s: z.number().int().min(0) }))
        .max(400)
        .optional(),
    created_by: uuid.nullish(),
});
export const schaakWoodpeckerRow = syncColumns.extend({
    id: uuid,
    set_no: z.number().int().min(1),
    cycle: z.number().int().min(1).max(20),
    started_at: isoTimestamp,
    finished_at: isoTimestamp.nullish(),
    position: z.number().int().min(0),
    correct: z.number().int().min(0),
    seconds: z.number().int().min(0),
    created_by: uuid.nullish(),
});
const username = z.string().regex(/^[A-Za-z0-9_-]{2,30}$/);
export const schaakAccountsRow = syncColumns.extend({
    id: uuid,
    lichess_username: username.nullish(),
    chesscom_username: username.nullish(),
    created_by: uuid.nullish(),
});
export const MISTAKE_KINDS = ['missed_mate', 'allowed_mate', 'hung', 'missed_capture', 'other'];
/** A pulled game (server-owned, read-only). */
export const schaakGameRow = z.object({
    id: uuid,
    user_id: uuid,
    source: z.enum(['lichess', 'chesscom']),
    url: z.string(),
    played_at: isoTimestamp,
    color: z.enum(['w', 'b']),
    opponent: z.string(),
    opponent_rating: z.number().int().nullable(),
    my_rating: z.number().int().nullable(),
    result: z.enum(['win', 'loss', 'draw']),
    speed: z.string(),
    opening: z.string().nullable(),
    analysed_at: isoTimestamp.nullable(),
    deleted_at: isoTimestamp.nullable(),
});
/** A pulled mistake (server-owned, read-only): a position from his own game. */
export const schaakMistakeRow = z.object({
    id: uuid,
    user_id: uuid,
    game_id: uuid,
    ply: z.number().int(),
    fen: z.string(),
    opp_move: z.string(),
    played: z.string(),
    played_san: z.string(),
    best: z.string(),
    best_san: z.string(),
    accept: z.array(z.string()),
    loss: z.number().int(),
    kind: z.enum(MISTAKE_KINDS),
    /** Stockfish's score from his side, before and after his move (null on rows from before 045). */
    eval_before_cp: z.number().int().nullish(),
    eval_before_mate: z.number().int().nullish(),
    eval_after_cp: z.number().int().nullish(),
    eval_after_mate: z.number().int().nullish(),
    /** Up to three moves better than his, best first; scores from his side. */
    better: z
        .array(z.object({
        move: z.string(),
        san: z.string(),
        cp: z.number().int().nullable(),
        mate: z.number().int().nullable(),
        pv: z.array(z.string()).optional(),
    }))
        .nullish(),
    /** The engine's best line after his move (046): why it was bad. */
    refutation: z.array(z.string()).nullish(),
    created_at: isoTimestamp,
    deleted_at: isoTimestamp.nullable(),
});
/** `POST /api/v1/schaak/import` — fetch new games now (analysis follows in the worker). */
export const schaakImportResponse = z.object({
    imported: z.number().int(),
    pending_analysis: z.number().int(),
    failed: z.array(z.object({ source: z.enum(['lichess', 'chesscom']), reason: z.string() })),
});
const mutationId = z.object({ id: uuid });
export const schaakMutation = z.union([
    mutationId.extend({ op: z.literal('upsert'), table: z.literal('schaak_cards'), row: schaakCardRow }),
    mutationId.extend({ op: z.literal('upsert'), table: z.literal('schaak_profile'), row: schaakProfileRow }),
    mutationId.extend({ op: z.literal('upsert'), table: z.literal('schaak_woodpecker'), row: schaakWoodpeckerRow }),
    mutationId.extend({ op: z.literal('upsert'), table: z.literal('schaak_accounts'), row: schaakAccountsRow }),
    mutationId.extend({
        op: z.literal('delete'),
        table: schaakSyncTable,
        row: z.object({ id: uuid, updated_at: isoTimestamp }),
    }),
]);
export const schaakPushRequest = z.object({ mutations: z.array(schaakMutation).max(500) });
export const schaakPushResponse = z.object({
    applied: z.array(uuid),
    rejected: z.array(z.object({ id: uuid, code: z.string(), message: z.string() })),
    server_time: isoTimestamp,
});
export const schaakPullResponse = z.object({
    server_time: isoTimestamp,
    changes: z.object({
        schaak_cards: z.array(z.record(z.unknown())),
        schaak_profile: z.array(z.record(z.unknown())),
        schaak_woodpecker: z.array(z.record(z.unknown())),
        schaak_accounts: z.array(z.record(z.unknown())),
        schaak_games: z.array(z.record(z.unknown())),
        schaak_mistakes: z.array(z.record(z.unknown())),
    }),
    has_more: z.boolean(),
    cursor: z.string().optional(),
    full_resync: z.boolean().optional(),
});
//# sourceMappingURL=schemas.js.map